/* FOM · superficie de PRODUTOS.

   O QUE ESTA TELA CORRIGE (SPATIAL VISUAL v5, achado do gate v4):
   ate aqui a cadeia ponta a ponta era desenhada DEBAIXO DE CADA cartao de
   produto. A cadeia, porem, e derivada do PAI do produto — e os quatro
   produtos da RMC tem o mesmo pai. Resultado: quatro cadeias visualmente
   distintas e semanticamente IDENTICAS, e um leitor razoavel concluia "esta e
   a rota do IM". Nao e. E a cadeia do no que HOSPEDA os quatro.

   A tela passa a dizer isso com todas as letras: a cadeia aparece UMA VEZ, no
   topo, nomeando o hospedeiro (derivado do dado, nunca escrito a mao) e
   declarando que vale igualmente para todos os produtos.

   O QUE O DADO SUSTENTA, e nada alem disso:
     · o produto e um no real (`func`) — id, nome, classe epistemica, resumo,
       fonte declarada, conhecimento e lacunas;
     · a UNICA aresta que um produto tem e `utiliza modalidade` (kind `trans`);
     · nenhum produto participa de relacao `flow`.
   Por isso NAO ha rota por produto, nao ha sequencia, nao ha origem nem
   destino, e esta tela nao desenha nenhum dos tres. O agrupamento e pela
   modalidade que o proprio produto declara. */
import type { FomNode, Relation } from '../core/types.ts';
import { COR_ST, ROTULO_ST, rotuloTipo} from '../core/types.ts';
import {
  PRODUTOS, derivarCadeiaDoProduto, hospedeiroDaCadeia, agruparProdutosPorModalidade,
} from '../dominio/dimensoes.ts';
import { el, botao } from './dom.ts';

export type ProdutosOpcoes = {
  aoEntrar: (externalId: string) => void;      /* abre a cena de conhecimento do produto */
  aoIrParaNo: (externalId: string) => void;
  aoFechar: () => void;
};

export function montarProdutos(host: HTMLElement, o: ProdutosOpcoes) {
  host.classList.add('superficie-produtos');
  host.hidden = true;
  host.setAttribute('role', 'region');
  host.setAttribute('aria-label', 'Produtos');

  function render(nos: FomNode[], relacoes: Relation[]): void {
    host.innerHTML = '';
    const porId = new Map(nos.map((n) => [n.external_id, n]));
    const cabecalho = el('header', 'cc-cabecalho');
    const t = el('div');
    t.appendChild(el('p', 'cc-eyebrow', 'Workspace · Produtos'));
    t.appendChild(el('h1', 'cc-titulo', 'As frentes reais e a modalidade que cada uma declara'));
    t.appendChild(el('p', 'cc-sub', 'Os produtos sao nos reais da BASE. A unica aresta que eles tem e a modalidade que declaram — nao ha rota, sequencia, origem nem destino por produto, e nada disso e desenhado aqui.'));
    cabecalho.appendChild(t);
    const acoes = el('div', 'cc-acoes');
    acoes.appendChild(botao('Voltar ao mapa · Esc', o.aoFechar, 'botao botao-principal'));
    cabecalho.appendChild(acoes);
    host.appendChild(cabecalho);

    const reais = (PRODUTOS as readonly string[])
      .map((id) => porId.get(id))
      .filter((n): n is FomNode => !!n && !n.removed_at);
    if (!reais.length) {
      host.appendChild(el('p', 'estado-vazio-titulo', 'Nenhum produto/fluxo mapeado neste mapa.'));
      return;
    }

    /* ---------------------------------------------------------- CONTEXTO --
       A cadeia aparece UMA VEZ e diz de quem e. O hospedeiro sai do proprio
       elo de papel `hospeda`; se a cadeia nao existir, o bloco inteiro nao
       aparece — vazio honesto, nunca cadeia suposta. */
    const cadeia = derivarCadeiaDoProduto(nos, relacoes, reais[0]!.external_id);
    const hospeda = hospedeiroDaCadeia(cadeia);
    if (cadeia.length && hospeda) {
      const ctx = el('section', 'produtos-contexto');
      ctx.setAttribute('aria-label', 'Cadeia do no que hospeda os produtos');
      ctx.appendChild(el('p', 'cc-eyebrow', 'Contexto compartilhado'));
      const titulo = el('h2', 'produtos-contexto-titulo');
      titulo.appendChild(el('span', 'produtos-contexto-rotulo', 'Cadeia de'));
      const alvo = botao(hospeda.name, () => o.aoIrParaNo(hospeda.external_id), 'produtos-contexto-no');
      alvo.title = 'abrir ' + hospeda.name + ' no mapa';
      titulo.appendChild(alvo);
      ctx.appendChild(titulo);
      ctx.appendChild(el('p', 'produtos-contexto-aviso',
        `Esta cadeia pertence a ${hospeda.name} e e a MESMA para os ${reais.length} produtos abaixo. `
        + 'Ela nao e a rota de nenhum produto individual: nenhum produto participa de relacao de fluxo na BASE.'));
      const trilho = el('div', 'cadeia-trilho');
      cadeia.forEach((elo, i) => {
        if (i > 0) trilho.appendChild(el('span', 'cadeia-seta', '›'));
        const b = el('button', 'cadeia-elo cadeia-elo-' + elo.papel);
        b.type = 'button';
        b.appendChild(el('span', 'produto-elo-papel', elo.papel === 'hospeda' ? 'hospeda o fluxo' : elo.papel));
        b.appendChild(el('b', undefined, elo.no.name));
        if (elo.relacao) b.appendChild(el('span', 'produto-elo-meta', elo.relacao));
        b.addEventListener('click', () => o.aoIrParaNo(elo.no.external_id));
        trilho.appendChild(b);
      });
      ctx.appendChild(trilho);
      ctx.appendChild(el('p', 'cc-nota', 'Derivada exclusivamente das relacoes reais da BASE; elo ausente e dito, nao desenhado. Os elos produto → comercial e geracao → handoff etapa-a-etapa nao existem como relacao.'));
      host.appendChild(ctx);
    }

    /* -------------------------------------------------------- MODALIDADES --
       Agrupamento derivado da unica aresta que o produto tem. O modal aparece
       UMA VEZ por grupo, como o objeto compartilhado que e — nunca duplicado
       dentro de cada produto. */
    const grupos = agruparProdutosPorModalidade(nos, relacoes);
    for (const g of grupos) {
      const bloco = el('section', 'modalidade-bloco');
      const cab = el('header', 'modalidade-cab');
      if (g.modal) {
        cab.appendChild(el('p', 'cc-eyebrow', g.relacao ?? 'modalidade'));
        const bm = botao(g.modal.name, () => o.aoIrParaNo(g.modal!.external_id), 'modalidade-titulo');
        bm.title = 'abrir ' + g.modal.name + ' no mapa';
        cab.appendChild(bm);
        const meta = el('div', 'modalidade-meta');
        const chip = el('span', 'ev-chip ev-mini', ROTULO_ST[g.modal.st]);
        chip.style.setProperty('--cor', COR_ST[g.modal.st]);
        meta.appendChild(chip);
        const tipo = rotuloTipo(g.modal.node_type);
        if (tipo) meta.appendChild(el('span', 'etiqueta', tipo));
        const itens = nos.filter((n) => n.parent_external_id === g.modal!.external_id && !n.removed_at).length;
        if (itens) meta.appendChild(el('span', 'produto-elo-meta', `${itens} itens`));
        cab.appendChild(meta);
        if (g.compartilhado) {
          cab.appendChild(el('p', 'modalidade-aviso',
            `Objeto unico, declarado por ${g.produtos.length} frentes — nao ha uma copia por produto.`));
        }
      } else {
        cab.appendChild(el('p', 'cc-eyebrow', 'sem modalidade declarada'));
        cab.appendChild(el('p', 'produto-ausente', 'Nenhuma relacao de modalidade na BASE para estas frentes.'));
      }
      bloco.appendChild(cab);

      const grade = el('div', 'produtos-grade');
      for (const p of g.produtos) {
        const card = el('article', 'produto-card');
        card.dataset.id = p.external_id;
        const cabCard = el('div', 'produto-cab');
        const capsula = el('button', 'produto-capsula');
        capsula.type = 'button';
        capsula.style.setProperty('--cor', COR_ST[p.st]);
        capsula.textContent = p.name;
        capsula.title = 'entrar no conhecimento deste produto';
        capsula.addEventListener('click', () => o.aoEntrar(p.external_id));
        cabCard.appendChild(capsula);
        const chip = el('span', 'ev-chip', ROTULO_ST[p.st]);
        chip.style.setProperty('--cor', COR_ST[p.st]);
        cabCard.appendChild(chip);
        if (p.node_type) cabCard.appendChild(el('span', 'etiqueta', (rotuloTipo(p.node_type) ?? 'sem tipo') + ' · produto'));
        card.appendChild(cabCard);

        const contas = el('div', 'produto-contas');
        contas.appendChild(el('span', 'produto-conta produto-conta-know', `◆ ${p.know.length} conhecimento(s)`));
        contas.appendChild(el('span', 'produto-conta produto-conta-gap', `◇ ${p.gaps.length} lacuna(s)`));
        card.appendChild(contas);

        if (p.summary) card.appendChild(el('p', 'produto-resumo', p.summary));
        if (p.src) card.appendChild(el('p', 'cc-nota xs', 'fonte declarada: ' + p.src));

        const rodape = el('div', 'cc-acoes');
        rodape.appendChild(botao('Conhecimento do produto', () => o.aoEntrar(p.external_id), 'botao botao-principal'));
        rodape.appendChild(botao('Ver no mapa', () => o.aoIrParaNo(p.external_id), 'botao'));
        card.appendChild(rodape);
        grade.appendChild(card);
      }
      bloco.appendChild(grade);
      host.appendChild(bloco);
    }

    host.appendChild(el('p', 'cc-nota',
      'Parceiros por produto e etapas por produto nao sao derivaveis: nao existem como relacao na BASE.'));
  }

  return {
    render,
    mostrar(): void { host.hidden = false; },
    esconder(): void { host.hidden = true; },
    get aberta(): boolean { return !host.hidden; },
  };
}
